import { useEffect, useRef, useContext, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector";
import MapContext from "../../../MapContext";
import { getFeatureWithId, getMapLayerById } from "../../../utils/mapUtils";
import { Style, Stroke, Circle as CircleStyle } from "ol/style";
import LayerPopup from "./../LayerPopup";
import { inAndOut } from "ol/easing";
import { Button } from "@progress/kendo-react-buttons";
import { setResultsForFeature } from "../../../../redux/resultsSlice";
import { filterReadFields, makeArray } from "../../../../permissionUtils";
import { getCenter } from "ol/extent";
import DataLinkList, { DataLink } from "../../../DataLinks/DataLinkList";
import { setDataLinkData, setRelatedRecordData } from "../../../../redux/resultsSlice";
import { dataAPI, getCookie } from "../../../../api/axios";
import RelatedRecordDescription from "./RelatedRecordDescription";
import jwt_decode from "jwt-decode";
import Attachments from "../../../Attachments/Attachments";

import {
    editRelatedRecord,
    clearRelatedRecordEdits,
} from "../../../../redux/editingSlice";
import { setActiveTool } from "../../../../redux/toolSelectSlice";

// percentage for fitting polygons and lines to map view
const ratio = 50;

const FeatureDescription = () => {
    const tabIndex = useSelector((state) => state.toolSelect.sideBarTab);
    const [layer] = useSelector((state) => state.resultsData.resultsForFeature);

    const relatedRecordsData = useSelector(
        (state) => state.resultsData.relatedRecordsData
    );
    const results = useSelector((state) => state.resultsData);
    const [description, setDescription] = useState();
    const [readFields, setReadFields] = useState();
    const dispatch = useDispatch();
    const [dataLink, setDataLink] = useState();
    const [relatedRecords, setRelatedRecords] = useState();
    const vectorLayer = useRef();
    const { map } = useContext(MapContext);
    const layerId = useRef();
    layerId.current = layer.Id;
    const featureId = useRef();
    featureId.current = layer.Features[0];
    const authPermissions = useSelector(
        (state) => state.authorizedPermissions.fieldPermissions
    );
    const [feature, setFeature] = useState(
        getFeatureWithId(map, layerId.current, featureId.current)
    );

    useEffect(() => {
        if (tabIndex === 2) {
            let tempFeature = getFeatureWithId(map, layerId.current, featureId.current);
            if (!tempFeature) {
                getMapLayerById(map, layerId.current)
                    .getSource()
                    .once("featuresloadend", (e) => {
                        tempFeature = getFeatureWithId(
                            map,
                            layerId.current,
                            featureId.current
                        );
                        if (tempFeature) {
                            setFeature(tempFeature);
                        }
                    });
            }
            if (tempFeature) {
                setFeature(tempFeature);
            }
        }
    }, [tabIndex]);

    const token = getCookie("ast");
    let userGivenName = "Guest";
    let tokenRoles = ["GUEST"];
    if (token) {
        userGivenName = jwt_decode(token).preferred_username;
        tokenRoles = jwt_decode(token).realm_access.roles;
    }

    //Find layer for selected feature from state
    const vectorLayerProperties = useSelector((state) =>
        state.configData.vectorLayerProperties.find((l) => l.Id === layerId.current)
    );

    // Data link info from layer
    const dataLinksConfig = vectorLayerProperties?.DataLinks?.DataLink
        ? makeArray(vectorLayerProperties?.DataLinks?.DataLink)
        : [];

    // Filter data links by permissions
    const permissedDataLinks = dataLinksPermissionsFilter(dataLinksConfig, tokenRoles);

    const getDataLinkData = () => {
        fetchDataLinks(layerId.current, featureId.current, permissedDataLinks).then(
            (data) => {
                dispatch(
                    setDataLinkData({
                        id: layer.Features[0],
                        data: data,
                    })
                );
            }
        );
    };

    // Attachments info from layer
    /** @type {Object} */
    const layerAttachment = vectorLayerProperties.Attachments;

    //Filter attachments by permissions
    const filteredAttachements = attachmentsPermissionsFilter(
        layerAttachment?.Attachment,
        tokenRoles
    );
    // Call API for data links
    useEffect(() => {
        if (permissedDataLinks.length > 0) {
            getDataLinkData();
        }
        if (permissedRelatedRecords.length > 0) {
            getRelatedRecordData();
        }
        return () => {
            dispatch(setDataLinkData({}));
        };
    }, [layer, feature]);

    // Related Records info from layer
    const relatedRecordsConfig = vectorLayerProperties?.RelatedTables?.RelateTable
        ? makeArray(vectorLayerProperties?.RelatedTables?.RelateTable)
        : [];

    // Filter related records by permissions
    const permissedRelatedRecords = relatedRecordsPermissionsReadFilter(
        relatedRecordsConfig,
        tokenRoles
    );
    const permissedRelatedRecords_Create = relatedRecordsPermissionsCreateFilter(
        relatedRecordsConfig,
        tokenRoles
    );

    const getRelatedRecordData = () => {
        permissedRelatedRecords.forEach((element) => {
            fetchRelatedRecords(
                layerId.current,
                featureId.current,
                element.RelatedTableID
            ).then((data) => {
                dispatch(
                    setRelatedRecordData({
                        id: layer.Features[0],
                        data: data,
                    })
                );
            });
        });
    };

    // Close related record view on results change
    useEffect(() => {
        setDataLink(undefined);
        setRelatedRecords(undefined);
    }, [results]);

    useEffect(() => {
        layerId.current = layer.Id;
        featureId.current = layer.Features[0];
        setFeature(getFeatureWithId(map, layerId.current, featureId.current));
    }, [results, layer]);

    // Filter read fields based on permissions
    useEffect(() => {
        // Get fields from vector
        const vectorFields = vectorLayerProperties.Fields
            ? makeArray(vectorLayerProperties.Fields.Field)
            : [{ Name: vectorLayerProperties.DisplayName }];

        // Filter read fields
        const readFields = filterReadFields(
            vectorFields,
            authPermissions,
            layerId.current
        );
        if (readFields[0]?.DisplayIndex !== undefined) {
            readFields.sort((a, b) => (a.DisplayIndex > b.DisplayIndex ? 1 : -1));
        }
        setReadFields(readFields);
    }, [layer]);

    // Draw and undraw feature
    useEffect(() => {
        if (!feature) {
            console.warn("feature not detected on map");
            return;
        }
        const geometry = feature.getGeometry();
        const extent = geometry.getExtent();
        const view = map.getView();
        if (geometry.getType() === "Point") {
            const zoom = view.getZoom();
            view.animate({
                center: getCenter(extent),
                zoom: zoom < 20 ? 20 : undefined,
            });
        } else {
            view.fit(extent, {
                size: determineSize(map, ratio),
                duration: 750,
                easing: inAndOut,
            });
        }
        vectorLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: highlightStyle,
            zIndex: 100,
        });
        vectorLayer.current.getSource().addFeature(feature);
        const description = parseFeatureDescription(
            vectorLayerProperties,
            feature.values_
        );
        setDescription(description);
        return () => {
            vectorLayer.current.getSource().clear();
        };
    }, [layer, feature]);

    const createRelatedRecords = (relatedRecordsConfig) => {
        dispatch(clearRelatedRecordEdits());
        dispatch(
            editRelatedRecord({
                RelatedRecordInfo: {
                    relatedRecordInfo: relatedRecordsConfig,
                    layerInfo: vectorLayerProperties,
                },
                RelatedRecordData: { record: undefined },
            })
        );
        dispatch(setActiveTool("editrelatedrecord"));
    };

    return (
        <>
            {dataLink && <DataLink dataLink={dataLink} setDataLink={setDataLink} />}
            {relatedRecords && (
                <RelatedRecordDescription
                    data={relatedRecords}
                    setData={setRelatedRecords}
                    feature={feature}
                    layerId={layerId.current}
                />
            )}
            <div
                style={dataLink || relatedRecords ? { display: "none" } : null}
                id="feature-description">
                {results.resultsForLayer.length > 0 && (
                    <Button
                        style={{
                            position: "absolute",
                            zIndex: "20",
                            cursor: "pointer",
                        }}
                        icon="arrow-left"
                        onClick={() => {
                            dispatch(setResultsForFeature([]));
                        }}></Button>
                )}

                <div className="results-title">{layer.DisplayName}</div>
                <div className="results-options">
                    <LayerPopup feature={feature} layerId={layerId.current} />
                </div>
                <div className="description-header">Description</div>
                <div className="feature-description">{description}</div>
                <div className="description-header">Details</div>
                {permissedDataLinks.length > 0 && (
                    <DataLinkList
                        dataLinkConfig={permissedDataLinks}
                        setDataLink={setDataLink}
                    />
                )}

                {filteredAttachements && (
                    <Attachments
                        layerId={layerId.current}
                        featureId={featureId.current}
                        AttachmentsConfig={layerAttachment}
                    />
                )}

                {readFields !== undefined && feature != null ? (
                    readFields.map((f) => {
                        if (!f.Visible) return;
                        const displayValue = formatFieldValue(f, feature.values_);
                        return (
                            <div key={f.Name}>
                                <div className="results-field">
                                    {f.DisplayName ?? f.Name}
                                </div>
                                <div className="results-field-value">{displayValue}</div>
                            </div>
                        );
                    })
                ) : (
                    <div />
                )}
                {permissedRelatedRecords.length > 0 &&
                    permissedRelatedRecords.map((rr) => {
                        return (
                            <div key={rr.RelatedTableID}>
                                <div className="results-field">{rr.DisplayName}</div>

                                {/*List of selectable values with feature description*/}
                                <ul style={{ listStyleType: "none", padding: 0 }}>
                                    {relatedRecordsData?.data &&
                                        !relatedRecords &&
                                        relatedRecordsData?.data[rr.RelatedTableID]?.map(
                                            (rrd) => {
                                                return (
                                                    <li
                                                        key={rrd[rr.Primarykey]}
                                                        className="feature-relatedRecord-label"
                                                        onClick={() =>
                                                            setRelatedRecords({
                                                                config: rr,
                                                                data: rrd,
                                                            })
                                                        }>
                                                        {parseFeatureDescription(rr, rrd)}
                                                    </li>
                                                );
                                            }
                                        )}
                                    {permissedRelatedRecords_Create &&
                                        permissedRelatedRecords_Create.length > 0 && (
                                            <li
                                                className="feature-relatedRecord-label"
                                                onClick={() => createRelatedRecords(rr)}>
                                                Create {rr.DisplayName}
                                            </li>
                                        )}
                                </ul>
                            </div>
                        );
                    })}
            </div>
        </>
    );
};

export default FeatureDescription;

/**
 * @typedef {ResultsForLayer[]} MultiLayerResults
 */

/**
 * @typedef {Object} ResultsForLayer
 * @property {string} DisplayName Property from layer config
 * @property {string} FeatureLabel Property from layer config
 * @property {string} FeatureDescription Property from layer config
 * @property {string} Id Property from layer config, unique identifier
 * @property {Array} Features Array of feature Ids of selected features,
 *                   will be array of one item is single featuer is being viewed
 */

// When single feature is selected
// Example of input:
/*
[{
    "DisplayName": "Parcel",
    "FeatureLabel": "{LayerDisplayName}",
    "FeatureDescription": "{PrimaryNameOne}<br/>{PropertyAddress}",
    "Id": "MbNYA6",
    "Features": [
        "3165"
    ]
}]

*/

/**
 * Parent function that takes in field and feature, and outputs a formatted value depending on field
 * @param {object} field Field for layer from config stored in redux state
 * @param {object} featureValues _values from Ol map feature
 * @returns {string} Value to display for field
 */
export const formatFieldValue = (field, featureValues) => {
    const lowerCaseValues =
        featureValues[
            Object.keys(featureValues).find(
                (k) => k.toLowerCase() === field.Name.toLowerCase()
            )
        ];
    if (field.Type === "Date") {
        return formatDate(field, lowerCaseValues);
    } else {
        return checkForCodedDomain(field, lowerCaseValues);
    }
};

/**
 * Formats date field depending on a "Format" attribute in config file
 * Format can either be "MM/DD/YYYYThh:mm:ss" or "MM/DD/YYYY", defaults to "MM/DD/YYYY"
 * @param {object} field Field for layer from config stored in redux state
 * @param {object} featureValue value from Ol map feature containing date in UTC seconds
 * @returns {string} Value to display for field
 */
const formatDate = (field, featureValue) => {
    const dateValue = featureValue;
    // Make sure date is integer
    if (!Number.isInteger(dateValue)) return "";
    // Convert to milliseconds
    const date = new Date(dateValue);
    // Check formatting
    if (field?.Format?.toLowerCase() === "mm/dd/yyyythh:mm:ss") {
        return date.toLocaleString();
    } else {
        return date.toLocaleDateString();
    }
};

/**
 * Check for coded domain and get coded value
 * @param {object} field Field of feature schema from config
 * @param {object} featureValue value from Ol map feature containing date in UTC seconds
 * @returns {string} Value to display for field
 */
const checkForCodedDomain = (field, featureValue) => {
    const domains = field?.Domains?.Domain ? makeArray(field.Domains.Domain) : undefined;
    featureValue = featureValue ?? "";
    if (!domains) return featureValue.toString();
    if (featureValue === "") return featureValue;

    const domain = domains?.find((d) => d.Code === featureValue);
    const displayValue = domain?.DisplayValue;

    if (displayValue == null) {
        console.log(
            "DisplayValue missing from domain for field:",
            field.DisplayName ?? field.Name
        );
        return featureValue.toString();
    } else {
        return displayValue.toString();
    }
};

const highlightStyle = new Style({
    zIndex: 10,
    stroke: new Stroke({
        color: "#00FFFF",
        width: 3,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#00FFFF",
            width: 4,
        }),
    }),
});

export const parseFeatureDescription = (layer, feature) => {
    let jsx = layer?.FeatureDescription;
    if (!jsx || feature == null) {
        return <div />;
    }
    for (const property in feature) {
        if (typeof property === "string") {
            let displayValue = "";
            if (layer?.Fields && layer?.Fields.Field) {
                let fieldInfo = makeArray(layer?.Fields.Field).find(
                    (i) => i.Name.toLowerCase() === property.toLowerCase()
                );
                displayValue = feature[property] ?? "";
                if (fieldInfo) {
                    displayValue = formatFieldValue(fieldInfo, feature);
                }
            } else {
                displayValue = feature[property] ?? "";
            }
            jsx = jsx.replaceAll(`{${property}}`, displayValue);
        }
    }
    // In case of properties that were not in geojson
    //   but were in sld, replace with empty string
    jsx = jsx.replaceAll(/\{.*\}/g, "");
    return <div dangerouslySetInnerHTML={{ __html: jsx }} />;
};

export const determineSize = (map, ratio) => {
    const vp = map.getViewport();
    const width = vp.offsetWidth;
    const height = vp.offsetHeight;
    if (width < height) {
        const screenRatio = width * (ratio / 100);
        return [screenRatio, screenRatio];
    } else {
        const screenRatio = height * (ratio / 100);
        return [screenRatio, screenRatio];
    }
};

/**
 *
 * @param {string} layerId Id for layer with datalinks
 * @param {string} featureId Id for specific feature (number as string)
 * @param {Array<object>} permissedDataLinks Datalink configuration
 * @returns {object | "No Data"} Object with each property representing a datalink
 *  in format {DataLinkID: [data]}. If response has no data "No Data" is returned
 */
export const fetchDataLinks = async (layerId, featureId, permissedDataLinks) => {
    let res;
    try {
        res = await dataAPI.get(`/Layers/${layerId}/Data/${featureId}/DataLink`); // return {ID: [data]}, data can be empty
    } catch (error) {
        console.log(error);
    }
    const validLinkIds = permissedDataLinks.map((d) => d.DataLinkID);
    const data = {};
    validLinkIds.forEach((l) => {
        if (res?.data[l]) {
            Object.assign(data, { [l]: res.data[l] });
        }
    });
    return Object.keys(data).length > 0 ? data : "No Data";
};
/**
 * Retrieves attachments for a specific layer and feature.
 *
 * @param {string} layerId - The ID of the layer.
 * @param {string} featureId - The ID of the feature.
 * @return {Promise} A promise that resolves to the attachments data.
 */

/**
 *
 * @param {string} layerId Id for layer with datalinks
 * @param {string} featureId Id for specific feature (number as string)
 * @param {string} relatedRecordID Id for specific Related Records (number as string)
 * @param {Array<object>} permissedRelatedRecords Datalink configuration
 * @returns {object | "No Data"} Object with each property representing a datalink
 *  in format {DataLinkID: [data]}. If response has no data "No Data" is returned
 */
export const fetchRelatedRecords = async (layerId, featureId, relatedRecordID) => {
    let res;
    try {
        res = await dataAPI.get(
            `/Layers/${layerId}/Data/${featureId}/RelatedRecords/${relatedRecordID}/Data`
        ); // return {ID: [data]}, data can be empty
    } catch (error) {
        console.log(error);
    }
    const data = {};
    Object.assign(data, { [relatedRecordID]: res.data });
    return Object.keys(data).length > 0 ? data : "No Data";
};

const attachmentsPermissionsFilter = (attachmentsConfig, permissions) => {
    if (!attachmentsConfig) return;
    const filteredAttachements = makeArray(attachmentsConfig).filter((d) => {
        const isRestriction = makeArray(d?.Permissions?.Permission).some((l) => {
            return permissions.includes(l?.Value) && l?.Read === false;
        });
        return !isRestriction;
    });

    return filteredAttachements;
};

// Show data links based on permissions
const dataLinksPermissionsFilter = (dataLinksConfig, permissions) => {
    if (!dataLinksConfig) return;
    return dataLinksConfig.filter((d) => {
        const hasFalse = makeArray(d?.Permissions?.Permission).some(
            (l) => permissions.includes(l?.Value) && l?.Read === false
        );
        return !hasFalse;
    });
};
// Show related records based on permissions
const relatedRecordsPermissionsReadFilter = (relatedRecordConfig, permissions) => {
    if (!relatedRecordConfig) return;
    return relatedRecordConfig.filter((d) => {
        return makeArray(d?.Permissions?.Permission).some((l) => {
            return permissions.includes(l?.Value) && l?.Read === true;
        });
    });
};
const relatedRecordsPermissionsCreateFilter = (relatedRecordConfig, permissions) => {
    if (!relatedRecordConfig) return;
    return relatedRecordConfig.filter((d) => {
        return makeArray(d?.Permissions?.Permission).some((l) => {
            return (
                permissions.includes(l?.Value) &&
                l?.Read === true &&
                l?.CreateRecord === true
            );
        });
    });
};
